/**
 * Cart slice — services the user has added but not yet paid for.
 *
 * Session 19 change: the customer app no longer pays in-app. Instead a service
 * is ADDED TO THE CART, and payment happens on the public landing cart page
 * (candleai.org/cart), which the user is handed off to with their phone +
 * current clinic pre-filled (and OTP already satisfied — they're logged in
 * here). This slice is the in-app cart that accumulates the chosen services
 * until that handoff.
 *
 * Why Redux (client state), like clinicSlice: the cart is a per-session UI
 * selection, not server data. It's intentionally NOT persisted (mirrors the
 * in-memory access token / current clinic) and is cleared on logout.
 *
 * Item shape: { id, name (string | {fa,en}), priceToman }
 *   `name` is kept bilingual so the cart list localizes instantly; `priceToman`
 *   powers the client-side proforma preview (base + 10% VAT). Toggle semantics
 *   — a service appears at most once (add is idempotent by id).
 */

import { createSlice } from '@reduxjs/toolkit';
import { logout } from '@/store/authSlice';

/** @type {{ items: Array<{ id: string, name: any, priceToman: number }> }} */
const initialState = {
  items: [],
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /** Add a service (no-op if already in the cart). */
    addItem(state, action) {
      const { id } = action.payload;
      if (!state.items.some((it) => it.id === id)) {
        state.items.push(action.payload);
      }
    },
    /** Remove a service by id. */
    removeItem(state, action) {
      state.items = state.items.filter((it) => it.id !== action.payload);
    },
    /** Empty the cart (e.g. after handoff to the payment page). */
    clearCart(state) {
      state.items = [];
    },
  },
  // Clear on logout/inactivity/refresh-failure so a new session starts fresh.
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.items = [];
    });
  },
});

export const { addItem, removeItem, clearCart } = cartSlice.actions;

export default cartSlice.reducer;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartCount = (state) => state.cart.items.length;
export const selectIsInCart = (id) => (state) =>
  state.cart.items.some((it) => it.id === id);
